/**
 * `AnalyticsCustomize` — the COMPONENT library, as a docked right sidebar.
 *
 * ## The user-facing name is "Layout", and it lives in ONE constant
 *
 * This is the FOURTH name for this thing — Widgets, Cards, Components, now Layout — and
 * the first two were renamed by find-and-replace across prose, which is why the second
 * rename missed occurrences. So every user-visible string is in `ANALYTICS_LIBRARY_COPY`
 * below, and a fifth rename is one object literal rather than a sweep.
 *
 * The word is borrowed from the tables' own `LayoutOptions` control, which does column
 * visibility + order + density — which things show and in what order. That is this
 * panel's job for cards, so it is parity rather than a fourth synonym.
 *
 * ⚠️ **The INTERNAL vocabulary stays "card" on purpose, and the mismatch is deliberate.**
 * `AnalyticsCardItem`, `ANALYTICS_CARD_CATEGORIES`, `cards[]`, `AnalyticsCardShell`, and —
 * the one that actually matters — the persisted `AnalyticsViewState` written into user
 * preferences all say card. Renaming the identifiers would either break every saved view
 * or require a migration, in exchange for nothing a user can see. A UI label and a storage
 * key are allowed to differ; a storage key and its own history are not.
 *
 * ## Why this is a SIDEBAR and not the dropdown it used to be
 *
 * The reason is structural, not aesthetic. **A popover cannot be a drag source.** It
 * lives in a portal, it closes on outside interaction, and the gesture that would add
 * a card by dragging it onto the canvas begins with exactly that — an interaction
 * outside the menu. The predecessor could therefore only ever offer a checkbox: tick
 * a card on and it appears wherever the layout algorithm decides. With a real grid
 * behind it, "where" is the interesting half of the question, and only a surface that
 * survives the drag can let the user answer it.
 *
 * The same argument decides sidebar-versus-card, and it is the reason this is not the
 * inset rounded panel it was first built as. A drag source that lives inside the
 * scrolling content region can SCROLL AWAY MID-DRAG — the pointer is captured, the
 * page moves under it, and the row the user is holding is no longer anywhere. There is
 * no recovery from that except letting go and starting again. A panel that is STICKY to
 * the viewport cannot do it, whatever its insets — pinning is what carries this property,
 * not being flush to the window edge.
 *
 * ## The chrome is Ask Vera's, deliberately
 *
 * `components/navigation/AskPanel` already solved a docked right sidebar on this
 * platform, and its central decision is the one this needs too: it is a FLEX SIBLING
 * of the content, so opening it makes the canvas narrower rather than covering it. No
 * backdrop, no focus trap, no scroll lock — you can hold the library open, look at the
 * dashboard, and drag between the two. A MUI `Drawer` (the obvious reach) is modal by
 * default and would take all three away, which would put us back where the popover
 * was. Same `component="aside"`, same `background.paper` fill, same title row ending in a
 * `PanelRightCloseIcon` control.
 *
 * It differs from Ask in three respects on purpose. This panel is PAGE-scoped, so it is
 * rendered by the Analytics page inside its own flex row rather than by `Layout` (Ask is a
 * companion to every page and has to live above the router) — which is also why it cannot
 * run the full height of the window and is inset instead. It is a fixed width, because Ask
 * is resizable for a conversation whose useful width varies with what is in it and a
 * fixed-content list has no such range. And it carries a full border and the card radius
 * rather than Ask's single `borderLeft`, which follows from that inset — see the `sx`.
 *
 * ## Both ways in, because drag alone is not an affordance
 *
 * Every row can be dragged onto the canvas AND has an add button. Drag-only would be
 * unreachable by keyboard, invisible to anyone who does not think to try it, and — a
 * practical matter — unverifiable in an automated pass, since a drag needs a live
 * renderer. The button is the accessible path and the honest fallback; the drag is the
 * one that lets you choose the position.
 *
 * ⚠️ A row already on the canvas STAYS draggable (VERA-260 — reversed from the original
 * "withdraw the affordance" design). The `+`/`X` control still toggles the metric's own
 * bare card, exactly as before; what changed is that dragging the row no longer only
 * means "add a second copy of this exact card." Since VRG-467, the SAME drag also
 * composes the metric onto an existing chart it is dropped on
 * (`AnalyticsCanvas.isComposeDropTarget`), and a metric can legitimately sit on several
 * charts at once — so withdrawing the grip the moment the metric's own tile is added
 * blocked the one gesture VRG-467 exists for. The only remaining refusal is per-TARGET
 * (a card cannot gain the same metric as a series twice), which `isComposeDropTarget`
 * already enforces; "has this metric been added somewhere" is no longer a reason to
 * disable the grip. See `AnalyticsDisplayPicker`'s identical fix on its own chips.
 *

 * ## ONE ROW PER METRIC, with display as a property of it (VRG-481)
 *
 * The library used to list `(metric x display)` PAIRS as separate rows, and every
 * complaint about this panel followed from that one decision:
 *
 *   - `Total Scans`, `Total Scans over time` and the legacy `Scans by time` were three
 *     rows for one measurement;
 *   - the `over time` / `by location` suffixes existed only to tell those siblings apart,
 *     so they restated the card TYPE inside the card's NAME;
 *   - a row carried a `Metric` badge top-right AND an "Also as: Chart | Map" row, so two
 *     controls described one axis and appeared to disagree.
 *
 * Now a row IS a metric and the `Display as` chips carry the display. Concretely, in this
 * file: variant ids are filtered OUT of the list (`metricRows`), and the top-right badge
 * is gone — the chips are the state, so there is nothing left for it to conflict with.
 *
 * A consequence worth stating rather than discovering: **every row is now a Metric**, so
 * the type filter had to become a DISPLAY filter or become a control with one option that
 * can only ever return everything. See `displays` below.
 */
import React, { useCallback, useMemo, useState } from 'react'

import { Box, IconButton, Stack, Tooltip, Typography, alpha, useTheme } from '@mui/material'
import type { SxProps, Theme } from '@mui/material'
import {
  GripVerticalIcon,
  PanelRightCloseIcon,
  PlusIcon,
  RotateCcwIcon,
  TableIcon,
  XIcon,
} from 'lucide-react'

import { Button } from '@repo/ui'
import { Search } from 'components/input/Search'
import { SelectField } from 'components/input/fields/SelectField'
import { AnalyticsCategoryGlyph } from 'components/display/AnalyticsCategoryLabel'
import { InfoTooltip } from 'components/feedback/InfoTooltip'
// The shared toolbar label-collapse mechanism (VRG-535). This button shares the `layout`
// rung with `LayoutOptions` — see the note on the label span below.
import { toolbarLabelSx } from 'components/input/ActionLine/toolbarCollapse'
// NOTE: `vhMinus` is no longer imported here — the panel's height moved up to the rail in
// `AnalyticsPage`, which owns the `calc` (see `PANEL_VIEWPORT_RESERVE_PX` below).

import { AnalyticsDisplayPicker } from './AnalyticsDisplayPicker'

import type { AnalyticsCardItem } from './AnalyticsCanvas'
import {
  ANALYTICS_CARD_CATEGORIES,
  ANALYTICS_CARD_CATEGORY_ORDER,
  parseAnalyticsVariantCardId,
  type AnalyticsCardCategory,
} from './analyticsGrid'
import { analyticsDisplayCapability, analyticsIsLibraryPresetCardId } from './analyticsDisplay'
// The chip's geometry, shared with the `Display as` chips in the row below (§36).
import { ANALYTICS_CHIP_GAP, ANALYTICS_CHIP_SX } from './analyticsChip'
import {
  ANALYTICS_SOLUTION_BLURBS,
  ANALYTICS_SOLUTION_ICONS,
  ANALYTICS_SOLUTION_LABELS,
  ANALYTICS_SOLUTION_ORDER,
  analyticsMetricSolutions,
  type AnalyticsSolutionCategory,
} from './analyticsSolutions'

/**
 * 393px — 340 plus one canvas column.
 *
 * Wide enough for a card's title and a one-line description at `body3` without the
 * description wrapping to three lines. Fixed rather than resizable: Ask is resizable
 * because a conversation's useful width varies with what is in it, and a fixed-content
 * list has no such range.
 *
 * ⚠️ This used to claim the width was chosen so "the canvas keeps 12 usable columns at
 * 1280 (1152 − 340 − 24 = 788px)". That was never true and the arithmetic was against the
 * wrong threshold: `ANALYTICS_GRID_BREAKPOINTS.lg` is 900px of CONTAINER width, so a 788px
 * canvas was already the 6-column `sm` breakpoint. Measured at a 1280 window with the panel
 * open, the canvas is 611px and the grid is 6 columns. Opening the library halves the grid;
 * that is the accepted trade, not an accident.
 *
 * ## WHY IT MOVED OFF 340 (VERA-260, second pass) — and why the earlier refusal was right
 *
 * Two notes in this epic argued AGAINST widening, and they are not being overruled so much
 * as superseded by a change to what the row holds. `AnalyticsDisplayPicker`'s header
 * measured that fitting its label and three chips INLINE needed ~375px and that each new
 * display type would cost another 70–89px, so five options would want ~514px; that
 * argument is untouched, and the fix it chose (label on its own line, chips get the full
 * content column) still stands. `SOLUTION_BADGE_CAP` then reached the same conclusion for
 * the badges. Both were answering "should the panel grow to fit ONE row?", and no is still
 * the answer to that question.
 *
 * What changed is that the row gained a SECOND wrapping chip row and then gave its badges
 * icons. At 340 that put five of the 28 rows on two badge lines before icons and eight
 * after — a third of a scrolling list where the card's height depends on how many
 * categories it happens to carry, which reads as inconsistent row rhythm rather than as
 * information. One column buys back most of it.
 *
 * ## The arithmetic, measured at a 1280 window with the panel open
 *
 *   canvas before          611px, 6 columns (611 < the 900px `lg` threshold)
 *   one 12-col column      (611 − 11 × 24) / 12 = 28.9, plus one 24px margin  =  52.9
 *   panel                  340 → 393
 *   canvas after           558px, still 6 columns — NO breakpoint change
 *   content column         264 → 317 (the panel less 76px of panel + card padding)
 *
 * ⚠️ WHAT THE CANVAS LOST, stated plainly because the canvas is where the work happens: a
 * 6-column grid column goes 81.8 → 73.0, so a 3-column stat tile goes 293.5 → 267.0. That
 * is an 8.7% narrower canvas and it clears the 220px tile-legibility floor
 * `ANALYTICS_GRID_BREAKPOINTS` derives, with room. Nothing reflows to a different column
 * count, and the panel is closed by default.
 *
 * ⚠️ THIS IS NOT A PRECEDENT FOR GROWING AGAIN. The next column costs the canvas the same
 * ~53px for a smaller and smaller share of the remaining wraps, and at some point a 3-col
 * tile crosses 220 and the grid stops being legible. If badge wrapping gets worse from
 * here the levers are the ones the two notes above already name — lower `SOLUTION_BADGE_CAP`,
 * or drop the badge icons back to label-only — not another column.
 */
export const ANALYTICS_CUSTOMIZE_PANEL_WIDTH = 393

/**
 * EVERY user-facing string this panel renders. See the header for why.
 *
 * Flat strings rather than a `noun` field the rest interpolates from. Templating them
 * looked tempting and is a trap: the noun appears in a title, a placeholder, three
 * sentences and four aria-labels, and those need different articles, plurals and casing —
 * so a single-token substitution produces "No component match that search" the first time
 * someone changes it. A rename is a find-and-replace WITHIN this object, which is the
 * property that was actually wanted: one place to look, not one token to change.
 */
/**
 * The panel's name, stated ONCE because two controls render it.
 *
 * A standalone const rather than a `get buttonLabel() { return this.title }` on the
 * object below: a getter inside an `as const` literal has to reason about `this`, which
 * is a needless typing hazard for a two-line indirection.
 */
const ANALYTICS_LIBRARY_NAME = 'Layout'

const ANALYTICS_LIBRARY_COPY = {
  /**
   * Panel title — and the button's label, which MUST be the same word.
   *
   * "Layout", the fourth name, and the first one borrowed rather than invented. The
   * platform's tables already have a control called Layout (`components/input/
   * LayoutOptions`) and it does column visibility, column ORDER and density — which
   * things show, and in what order. That is exactly this panel's job for cards, so the
   * word is genuine parity rather than a synonym reached for because the last three were
   * used up.
   *
   * ⚠️ The button and this title are ONE string for a reason: a control labelled
   * "Customize" opening a panel titled "Components" is two names for one thing, and the
   * user has to hold both. `buttonLabel` therefore reads from `title` and is not a
   * second literal that a future rename could miss.
   *
   * The ITEM noun stays "components" — the panel is the layout, the rows in it are the
   * components you place. `LayoutOptions` reads the same way: a panel called Layout
   * listing columns by name.
   */
  title: ANALYTICS_LIBRARY_NAME,
  /** The `aside`'s accessible name — what a screen reader announces on entering it. */
  landmark: 'Dashboard layout',
  searchPlaceholder: 'Search components',
  /**
   * The display filter's label — at rest AND once populated (VERA-260).
   *
   * "Display as", sentence case, matching the toolbar's "Certificate status": a control
   * that identifies itself bare when nothing is selected ("Display as") and keeps that
   * same label as a PREFIX once it is ("Display as: Chart", "Display as: Chart, Map"),
   * rather than the trigger falling back to a bare value with nothing saying which
   * dimension it belongs to — the same reason the Status toolbar control states its
   * label before its values instead of showing bare pills. `SelectField`'s `valuePrefix`
   * carries this at the call site.
   *
   * ⚠️ This previously argued for "Any display" on the grounds that "All types" named an
   * axis the list no longer varies on — that argument still holds (every row IS a
   * Metric now; see this file's header on VRG-481), but the label itself changed: "Any
   * display" only ever rendered AT REST, so a populated trigger read as a bare value
   * with no label at all ("Chart, Map"). "Display as" is what both states now share.
   */
  allTypes: 'Display as',
  /**
   * The SOLUTION filter's label — bare at rest, a prefix once populated, exactly as
   * `allTypes` is and for the same reason its note gives (VERA-260).
   *
   * "Solution", singular, because it labels the control ("filter by solution"), not the
   * selection. The plural would read as a claim that a metric has several — true of some
   * rows and not others, and the trigger already says which by listing them.
   */
  allSolutions: 'Solution',
  /**
   * ⚠️ SAID OUT LOUD, because multi-select does not disambiguate itself.
   *
   * Ticking Brand protection AND Consumer engagement is an OR — rows evidencing EITHER —
   * and the opposite reading (rows evidencing BOTH) is the one a reader is likelier to
   * assume from a checklist, since narrowing is what a filter is for. It is also the
   * reading under which most selections would return nothing: only seven of the
   * twenty-seven metrics carry two categories at all, so an AND of any two would usually
   * empty the list and look broken rather than strict.
   *
   * The display filter above takes the same OR and says nothing, which is defensible
   * there — "Chart, Map" plainly means either, because a metric being drawable two ways
   * is not a narrowing anyone would read as conjunctive. A solution is a property of the
   * metric itself, so the conjunctive reading is live and has to be closed.
   */
  solutionHelp: 'Showing components in any of the selected solutions.',
  /**
   * The badge row's label — the SAME word as the filter's, deliberately.
   *
   * The filter narrows on this property and the badge displays it, so they must name it
   * identically or a reader has to work out that "Solution" and (say) "Proves" are the
   * same axis. It mirrors `Display as`, which is likewise one word on both the filter and
   * the chips it corresponds to.
   */
  solutionLabel: 'Solution',
  solutionTooltip:
    'The part of Vera this figure is evidence for. A metric can serve more than one. Core figures are the raw counts other figures are worked out from.',
  /**
   * Empty states. GENERATED from which controls are active, not enumerated (VERA-260).
   *
   * There were three literals for two filters, which is every combination. A third filter
   * makes that seven, and the seventh sentence is where someone stops writing them and
   * reaches for a generic "No results" — losing the property these exist for, which is
   * that the message NAMES the control the reader has to undo. A reader who only picked
   * a solution must not be pointed at a search box they never touched.
   *
   * Composed as a list because the sentence has to stay readable at three clauses: "No
   * components match that search, can be shown that way, and are in those solutions" is
   * worse than the enumerated version it replaces, whereas a comma-joined list of the
   * active constraints is legible at any length.
   */
  emptyPrefix: 'No components',
  emptyBySearch: 'match that search',
  emptyByTypes: 'can be shown that way',
  emptyBySolutions: 'are in those solutions',
  /**
   * The toolbar trigger — the SAME string as the panel title, not a second literal.
   *
   * It used to be a verb ("Customize") on the argument that a button names an action
   * while a panel names a thing. That argument is fine in the abstract and was wrong
   * here: the two were the one place a rename could half-land, and it did — the button
   * said Customize through all three of the panel's names. A button that opens a panel
   * should say what the panel is called, so pressing it and reading the heading confirm
   * each other instead of raising a question.
   */
  buttonLabel: ANALYTICS_LIBRARY_NAME,
  buttonTooltip: 'Add and arrange components',
  buttonTooltipDisabled: 'Collapse the expanded card to change the layout',
  resetTooltip: "Reset layout — restore this view's components and arrangement",
  resetAria: 'Reset the dashboard layout',
  collapseTooltip: 'Collapse panel',
  collapseAria: 'Collapse layout panel',
  add: (name: string) => `Add ${name}`,
  remove: (name: string) => `Remove ${name}`,
  addAria: (name: string) => `Add ${name} to the dashboard`,
  removeAria: (name: string) => `Remove ${name} from the dashboard`,
} as const

/**
 * How much viewport height the panel gives up so it is never cut off, in px.
 *
 * 48, and every part of it was MEASURED on the running page rather than chosen — the two
 * numbers it is made of behave differently and guessing either produces a panel that looks
 * inset and is still clipped:
 *
 *   24  the top inset once pinned. `Layout`'s scroll container carries `padding: 24px`, and
 *       a sticky offset resolves against that padding edge, so `top: 0` already lands the
 *       panel 24px below the window. Measured: `top: 24px` pinned the panel at 48, not 24 —
 *       the CSS value and the visible inset are NOT the same number here.
 *   24  the distance the page's content box already ends above the window bottom, which is
 *       `Layout`'s padding. The panel's containing block stops there, so a panel taller than
 *       this is pushed up out of the top of its own sticky range at the end of the scroll.
 *       Measured before the original fix: bottom 751.8, top -0.2 — clipped.
 *
 * ⚠️ This was 72 (24 + 48) while `AnalyticsPage` carried its own `p={6}` ON TOP of `Layout`'s,
 * which made the page's gutter 48 and Analytics the only surface in the app rendering at
 * double the standard inset. That padding is gone, so the BOTTOM term is `Layout`'s 24 alone.
 * The TOP term is untouched — it was always `Layout`'s padding and never the page's. Both
 * halves come from the same 24 now, which is why the number is no longer two different ones
 * added together.
 *
 * The result is that the panel comes to rest exactly on the page's content box: 24 from the
 * window at the top, 24 at the bottom and 24 at the right, which is precisely where a card
 * on the canvas sits. That is what "aligned to the cards" resolves to once measured, and it
 * is why the panel no longer breaks out of the page gutter at all (it used to reclaim both
 * 24s with `mr: -48`, landing flush against the window with `borderRadius: 0` and a bare
 * `borderLeft` — which is what made it read as a slab sawn off at both ends).
 *
 * ⚠️ Residual, stated rather than papered over: at scroll position 0 the panel's natural top
 * is ~271px down the page (it is a flex sibling of the canvas, so it begins below the page
 * header and the action line) and its bottom therefore sits below the fold until you scroll
 * and it pins. That is ordinary sticky-rail behaviour and it is NOT fixable with a static
 * height — the panel would need to be two different heights before and after pinning. The
 * structural fix is to render it from `Layout` as `AskPanel` does, which would also cost it
 * the page-scoping this panel needs.
 */
export const PANEL_VIEWPORT_RESERVE_PX = 48

interface AnalyticsCustomizeButtonProps {
  open: boolean
  onToggle: () => void
  /** Disabled while a card is expanded: the canvas it edits is not on screen. */
  disabled?: boolean
}

/**
 * The toolbar trigger, beside Views.
 *
 * Same `@repo/ui` `Button size="large" variant="secondary"` as Views / Filter / Sort,
 * so it sits in the toolbar band as a peer rather than as a page action.
 *
 * ## The open state is PAINTED, not only announced
 *
 * This carried `aria-pressed={open}` and nothing else, on the reasoning that "the panel's
 * own presence on screen is the state indicator and a glyph that changed with it would be
 * saying the same thing twice." That argument is sound for a MENU — you cannot miss a
 * popover, because it is drawn over the thing you were looking at — and it fails here for
 * the reason this panel is not a menu: it is DOCKED, 340px away at the far right of the
 * page, and the canvas simply reflows around it. So the state was announced to screen
 * readers and invisible to everyone else, which is the one distribution of a signal that
 * cannot be defended.
 *
 * ## The treatment is NEUTRAL — the app's own primary ramp, deliberately not brand
 *
 * ⚠️ This shipped once as `brand[500]` border + a `0.1` brand tint, matching the engaged
 * rows INSIDE this panel and §34's "border = chosen" axis, and it was rejected: no purple
 * on this button. Do not reach for the brand hue again. The rows keep their brand edge —
 * they are a SELECTION among many, which is what §34 is about — while the trigger is a
 * single toggle whose only job is to say "the panel is showing".
 *
 * So the signal is CONTRAST AGAINST THE PAGE rather than hue, walked up the neutral ramp
 * the button already lives on. Both properties move, because the two states this sits
 * between are separated on both:
 *
 *   state    fill                    border
 *   rest     `fieldFill`             `primary[100]`
 *   hover    `primary[50]`           `primary[200]`
 *   OPEN     `primary[100]`          `primary[300]`
 *   open+hov `primary[200]`          `primary[300]`
 *
 * Measured in dark, where every one of the four is distinct — fills #1a1a1e / #18181b /
 * #27272a / #3f3f46, borders #27272a / #3f3f46 / #52525b / #52525b.
 *
 * The ramp is what makes this MODE-SYMMETRIC without a conditional. Its dark half ascends
 * in lightness (`50: #18181b` → `300: #52525b`) while its light half descends
 * (`50: #f6f6f6` → `300: #b0b0b0`), so one index means "further from the page background"
 * in BOTH modes. A literal colour, or MUI's `action.selected` overlay, would have needed a
 * mode check to say the same thing.
 *
 * Rejected, with the reason, so they are not re-proposed:
 *   `navSelected()`  brand-tinted by construction — the thing being removed.
 *   `navHover()`     IS `action.hover`; a pressed state cannot be the hover state.
 *   ToggleButton's   `background.paper` pill + shadow. Neutral and canonical, but it is an
 *   selected pill    ELEVATION metaphor that reads only against the recessed track it lifts
 *                    off. There is no track here, so the pill loses its ground.
 *   SegmentedChoice  also `brand[500]` border + `0.1` brand fill — out for the same reason.
 *
 * ⚠️ Nothing here changes the button's GEOMETRY — border width, padding and font are
 * untouched, only their colours. This control sits in the pinned toolbar band, so a
 * treatment that grew the button by even a pixel would shift the band's height on every
 * toggle and push the grid.
 *
 * The glyph is still not swapped, and that part of the original reasoning survives intact:
 * the surface carries the state, so a second icon would be the redundancy it warned about.
 */
export const AnalyticsCustomizeButton = ({
  open,
  onToggle,
  disabled = false,
}: AnalyticsCustomizeButtonProps) => {
  // A BLOCK body, where this was an implicit return. The open-state `sx` below needs the
  // palette and cannot use `sx={theme => …}` to reach it — see the note at that prop.
  const theme = useTheme()

  return (
  <Tooltip
    /*
      ⚠️ THE ENABLED TITLE NOW LEADS WITH THE BUTTON'S OWN LABEL, and that is a
      requirement rather than a flourish. The label span below collapses to icon-only as
      the toolbar narrows (VRG-512), and at that point the tooltip is the ONLY thing on
      screen carrying the word "Layout" — `LayoutOptions` makes exactly this argument for
      its own tooltip. The description is kept after it rather than replaced: it is the
      more useful half once the reader already knows which control they are on.

      Separator is a middot, not an em-dash (§15).

      The DISABLED title is left alone. It already names the layout concept, and the
      reason a control is dead is the more urgent thing to say when it is (§46).
    */
    title={
      disabled
        ? ANALYTICS_LIBRARY_COPY.buttonTooltipDisabled
        : `${ANALYTICS_LIBRARY_COPY.buttonLabel} · ${ANALYTICS_LIBRARY_COPY.buttonTooltip}`
    }
  >
    {/* A disabled MUI button emits no pointer events, so Tooltip needs a live wrapper
        or the explanation for the disabled state is unreachable (§46). */}
    <Stack>
      <Button
        id="analytics-customize-button"
        size="large"
        variant="secondary"
        disableElevation
        disabled={disabled}
        aria-pressed={open}
        aria-expanded={open}
        // The accessible name. The visible label below is `display: none` at a narrow
        // container, which removes it from the accessibility tree as well as the layout,
        // so without this the collapsed trigger announces as unnamed. Same fix
        // `LayoutOptions` and `ViewsDropdown` carry for the same reason.
        aria-label={ANALYTICS_LIBRARY_COPY.buttonLabel}
        onClick={onToggle}
        /*
          ⚠️ A PLAIN OBJECT, never `sx={theme => …}`. The shared `Button` merges
          `sx={{ ...finalStyles, ...sx }}` — a SHALLOW OBJECT SPREAD — and spreading a
          FUNCTION yields `{}`, because a function's own properties (`name`, `length`,
          `prototype`) are all non-enumerable. So the callback form type-checks, renders,
          and silently applies nothing. Hence the `useTheme()` above rather than the
          callback that would otherwise be the idiomatic way to reach the palette here.

          `border` and `background` are the exact property names the secondary variant
          sets, so these override rather than sit beside them — a key only wins under a
          shallow merge if it is spelled the same. `backgroundColor` would land alongside
          the variant's `background` shorthand and leave the result to declaration order.

          `&:hover` is likewise replaced wholesale, which is what a shallow merge does to a
          nested key — worth stating, because it means the variant's hover border has to be
          restated here. Without it an open button would fall back to `primary[200]` on
          hover and read as though it had switched off under the cursor.
        */
        sx={
          open && !disabled
            ? {
                border: `1px solid ${theme.palette.primary[300]}`,
                background: theme.palette.primary[100],
                '&:hover': {
                  border: `1px solid ${theme.palette.primary[300]}`,
                  background: theme.palette.primary[200],
                },
              }
            : undefined
        }
      >
        <Stack direction="row" spacing={1.5} alignItems="center">
          {/*
            The SAME glyph the table pages' Layout control uses (`LayoutOptions`), not a
            sliders icon. Both buttons are otherwise identical already — `variant="secondary"`,
            `size="large"`, a `spacing={1.5}` row, and the same collapse-to-icon-below-md
            label — so they were plainly meant to read as one control and only the glyph had
            diverged. Sliders says "adjust settings"; this opens a LAYOUT panel.

            Stroke and size match `LayoutOptions` exactly (strokeWidth 1, lucide's default
            24, i.e. no `size` prop) rather than the 18/1.5 this carried.

            ⚠️ Colour deliberately INHERITS rather than copying `LayoutOptions`' hardcoded
            `theme.palette.primary[800]`. A hardcoded ink does not dim when the button is
            disabled — which this button genuinely is while a card is expanded — so
            `currentColor` from the Button is the more correct of the two and the
            divergence is upward.

            Inheriting is also what lets the OPEN state reach the glyph for free if the ink
            is ever tinted: today the open treatment is border + fill only, and the mark
            stays `text.primary` exactly as the panel's own engaged rows do.

            (This note used to give a second reason — that the component had no `theme` in
            scope. It has one now, for the open-state `sx`; the reason above is the one that
            was ever load-bearing.)
          */}
          <TableIcon strokeWidth={1} />
          {/*
            Label collapses to icon-only as the TOOLBAR CONTAINER narrows — LAST of the row
            to go, on the shared `layout` rung (VRG-535, `toolbarCollapse.ts`).

            🚨 IT WAS A VIEWPORT QUERY AT `md`, AND THAT IS THE BUG (VRG-512). The comment
            this replaces claimed the label matched "Views/Filter/Sort" — it had not since
            VRG-535 moved all four of those onto container queries and left this one behind.
            A `md` breakpoint is 900px of VIEWPORT, and the sidebar is a 288px flex sibling
            rather than an overlay, so at the 1280 width this defect was reported at the
            viewport was comfortably over `md` while the toolbar container was ~943px and
            the row was two rows tall. The label never dropped, so the row wrapped instead.

            THE SAME RUNG AS `LayoutOptions`, deliberately: this button and that one are
            built to read as one control (same glyph, same variant, same size, same
            position right of Filter), so they must shed their labels at the same width or
            the app would have two Layout buttons disagreeing about when they collapse.

            No `toolbarStartIconSx` counterpart: the icon and label are siblings of this
            `Stack`, whose `spacing` emits the gap as a `margin-left` on the LABEL, so a
            hidden label takes its own gap out of the flow with it. Only MUI's `startIcon`
            (Download, Filter, Sort) leaves a margin behind on the icon itself.
          */}
          <Typography variant="body2" sx={toolbarLabelSx('layout')}>
            {ANALYTICS_LIBRARY_COPY.buttonLabel}
          </Typography>
        </Stack>
      </Button>
    </Stack>
  </Tooltip>
  )
}

/**
 * How many solution badges a row shows before collapsing the rest into `+N`.
 *
 * ## 2, and every number below was MEASURED in the browser at 1280x800
 *
 * The row's content column is **317px** at the panel's 393 (it was 264 at 340 — see
 * `ANALYTICS_CUSTOMIZE_PANEL_WIDTH` for what that column cost the canvas). Rendered at
 * 12/500 with `ANALYTICS_CHIP_SX`'s padding AND a 13px leading glyph, the five labels
 * measure:
 *
 *                                 label only    with glyph (+17)
 *   Brand protection                   108.3        125.3
 *   Resale & circularity               121.8        138.8
 *   Traceability & logistics           137.8        154.8
 *   Consumer engagement                140.3        157.3
 *   Sustainability & compliance        164.4        181.4
 *   (the `+N` chip — no glyph)          31.9         31.9
 *
 * Every chip is 24px tall and the row gap is 8, so a wrap costs the row 32px. Two badges
 * share a line iff their widths plus 8 fit the content column.
 *
 * ## ⚠️ THE ICONS COST THREE ROWS, and that is the trade to argue with
 *
 * Measured across the 28 library rows at the new 317px column:
 *
 *   badges without glyphs      28 of 28 on one line
 *   badges with glyphs         25 on one line, 3 wrap
 *
 * The three are `scans_by_country` (330.5), `log_product_lifespan` (328.2) and
 * `mkt_geo_reach` (320.1) — the last missing by 3.1px. At the old 264px column with glyphs
 * it would have been eight. So the column bought back five wraps and the glyphs spent
 * three of them; net 5 → 3 against where this started.
 *
 * ⚠️ THE FALLBACK IS LABEL-ONLY BADGES, and it is one line: drop the
 * `ANALYTICS_SOLUTION_ICONS` render from `SolutionBadges` and keep it in
 * `solutionOptions`, where a menu row has width to spare and the glyph still earns its
 * place. That takes every row to one line. It is NOT taken today because the glyph is
 * doing a second job the width table cannot show — see `ANALYTICS_SOLUTION_ICONS` on why a
 * glyph-less tag above a glyphed control row reads as unfinished rather than as different.
 * If the ragged row heights are judged worse than that, this is the lever.
 *
 * ⚠️ SHORTENING THE LABELS is NOT the lever, and it was rejected rather than missed.
 * Trimming to "Traceability" / "Sustainability" would fit every row on one line, and it
 * would give each category two names — the full one in the filter, a clipped one on the
 * badge — which is precisely the "two names for one thing" defect this panel's own header
 * records itself having shipped three times. The labels also come from
 * `METRIC_SOLUTION_LABELS`, i.e. the marketing site's own headings; a badge that renames
 * them locally is a second copy that drifts (§44).
 *
 * That is the whole trade, and 2 is where the CAP lands because of a fact about the DATA
 * rather than about the pixels. Across the 28 library rows the fan-out is 5 / 15 / 7 / 1:
 * five carry NO category (the primitives, `[]` — they render no badge row at all), fifteen
 * carry one, seven carry two, and exactly one — the `scans_by_country` choropleth, a metric
 * PICKER over three differently-purposed columns — carries three. So a cap of 2 renders 27
 * of 28 rows COMPLETE and collapses exactly one to `+1`. Capping at 1 would collapse a
 * quarter of the library to save a line that only some rows would have used.
 *
 * ⚠️ The cap is therefore load-bearing TODAY, not merely future-proofing — an earlier draft
 * of this note claimed `+N` was unreachable, which was true only while the choropleth was
 * missing from the classification altogether. It also has to stay right as the taxonomy is
 * corrected: `ANALYTICS_METRIC_SOLUTIONS` is a draft, and a third category landing on a
 * second metric must cost that row a chip rather than growing an unbounded row that pushes
 * the `Display as` controls down and makes one library row twice its neighbours' height.
 */
const SOLUTION_BADGE_CAP = 2

/**
 * ── WHICH DISPLAYS A LIBRARY ROW ANSWERS FOR — the type filter's real question ──────
 *
 * Two kinds of row, two different answers, and conflating them broke the filter (VERA-260).
 *
 * A METRIC row is a promise about what you COULD draw: its displays are the capable ones,
 * because ticking `Map` should surface every metric a map can be made of, whether or not
 * one is on the canvas.
 *
 * A PRESET row (`analyticsIsLibraryPresetCardId` — a variant card no `Display as` chip can
 * produce, e.g. the by-country map) is not a promise, it IS a map. Asking
 * `analyticsDisplayCapability` about its id gets `null` for every axis — a variant id has
 * no dimension, so every display comes back incapable — and the row would then be filtered
 * OUT by ticking the very display it is. It answers for exactly one: its own.
 *
 * `metric` is excluded from both: every row satisfies it, so it is the one option that
 * could never narrow anything, which is why it is not offered as a filter value either.
 */
const rowDisplays = (card: AnalyticsCardItem): AnalyticsCardCategory[] => {
  if (analyticsIsLibraryPresetCardId(card.id)) {
    return card.category && card.category !== 'metric' ? [card.category] : []
  }
  return ANALYTICS_CARD_CATEGORY_ORDER.filter(
    display => display !== 'metric' && analyticsDisplayCapability(card.id)[display].capable,
  )
}

/**
 * The solution badges for one library row.
 *
 * Renders NOTHING when a metric carries no categories. That is unreachable for any card in
 * the library — `ANALYTICS_METRIC_SOLUTIONS` is total over the id union, so every row has
 * at least one — but the empty case still has to be right, because the alternative is a
 * bare "Solution" label with nothing under it, which reads as a failed fetch rather than
 * as an absence. Same call `AnalyticsDisplayPicker` makes for a metric with no alternate
 * display, and for the same reason: the LABEL is part of what is conditional, not just the
 * chips.
 */
const SolutionBadges = ({ cardId }: { cardId: string }) => {
  const theme = useTheme()
  const carried = analyticsMetricSolutions(cardId)
  if (carried.length === 0) return null

  const shown = carried.slice(0, SOLUTION_BADGE_CAP)
  const hidden = carried.slice(SOLUTION_BADGE_CAP)

  const badgeSx = {
    ...ANALYTICS_CHIP_SX,
    /*
      ── FILLED AND EDGELESS: THE HALF THAT SAYS "THIS IS NOT A CONTROL" ──────────────
      ONE resting state, and no interactive affordances at all — no cursor change, no
      hover fill, not focusable. These are metadata, and a badge that lights up under the
      pointer is a promise that clicking does something. The `Display as` chips below DO
      respond, which is precisely what has to stay distinguishable.

      ⚠️ THIS USED TO BE `borderColor: 'primary.100'` ON A TRANSPARENT GROUND — i.e. the
      resting `Display as` chip, exactly. Two rows of identically-styled pills sat eight
      pixels apart, one passive and one clickable-and-draggable, with NOTHING on screen
      saying which was which; the reader had to click one to find out. That was the
      complaint this change exists to close, and the fix is to invert the treatment rather
      than to tweak it:

        badge (metadata)   quiet FILL, no visible edge, `cursor: default`, no hover
        chip  (control)    NO fill at rest, visible EDGE, `cursor: grab`, hover tint,
                           and a grip glyph on hover (`AnalyticsDisplayPicker`)

      Filled-vs-outlined is the oldest legible statement of tag-vs-button, and it survives
      greyscale and a 100ms glance, which "same pill, slightly different border grey" does
      not.

      `borderColor: 'transparent'` rather than `border: 'none'`, deliberately: the border
      stays in the box model, so the badge keeps `ANALYTICS_CHIP_SX`'s geometry to the
      pixel and the two rows stay on one rhythm. That module's whole contract is that
      GEOMETRY is shared and APPEARANCE is not (§36) — dropping the border outright would
      make a badge 2px shorter and 2px narrower than the chip below it, which is drift
      dressed as a style choice.

      The tint is derived from `text.primary` rather than being a palette grey so it holds
      in both themes — a fixed `primary[50]` is invisible on a dark card. 0.06 matches the
      row's own hover tint in weight, so a badge never out-weighs the title above it.
    */
    borderColor: 'transparent',
    backgroundColor: alpha(theme.palette.text.primary, 0.06),
    color: 'text.secondary',
    cursor: 'default',
  } as const

  return (
    /*
      `mt` here rather than on a parent `Stack spacing`: the three blocks below the title
      want DIFFERENT gaps (see the row's own note), which one uniform spacing cannot say.
      Owning it here also means the gap disappears with the component — a metric with `[]`
      solutions renders nothing at all and leaves no hole where this row would have been.
    */
    <Stack spacing={2} sx={{ mt: 2 }}>
      <Stack direction="row" alignItems="center" spacing={2}>
        <Typography variant="body3" color="text.secondary">
          {ANALYTICS_LIBRARY_COPY.solutionLabel}
        </Typography>
        {/* Literal `text`, not a glossary `term`: "solution" here is Vera's own product
            grouping — the marketing site's top-level areas — rather than a term of art
            with an entry, and `InfoTooltip`'s own guidance is that a concept with no entry
            takes literal text rather than a retyped definition (§44). */}
        <InfoTooltip text={ANALYTICS_LIBRARY_COPY.solutionTooltip} />
      </Stack>

      {/* `useFlexGap` + `gap`, matching the `Display as` row — `spacing` compiles to
          `margin-left`, which a wrapped chip carries onto the next line and indents it. */}
      <Stack direction="row" useFlexGap flexWrap="wrap" gap={ANALYTICS_CHIP_GAP}>
        {shown.map(solution => {
          // The ONE solution->glyph table (§36), consumed rather than re-picked — the same
          // mark this category wears in the filter's option row.
          const Glyph = ANALYTICS_SOLUTION_ICONS[solution]
          return (
            // The blurb, so a marketing heading beside an unfamiliar metric says whether it
            // describes the metric or the tab the metric came from.
            <Tooltip key={solution} title={ANALYTICS_SOLUTION_BLURBS[solution]}>
              <Box component="span" sx={badgeSx}>
                <Glyph size={13} strokeWidth={1.5} aria-hidden />
                {ANALYTICS_SOLUTION_LABELS[solution]}
              </Box>
            </Tooltip>
          )
        })}
        {hidden.length > 0 && (
          /* The overflow chip NAMES what it hides. A bare "+2" tells a reader something is
             missing and gives them no way to find out what, on a row whose whole purpose is
             to say what a metric is for.

             NO GLYPH, and that is not an oversight: it stands for a SET of categories, so
             any one solution's mark would be a claim about which, and five stacked marks
             would be wider than the labels they replace. */
          <Tooltip title={hidden.map(s => ANALYTICS_SOLUTION_LABELS[s]).join(', ')}>
            <Box component="span" sx={badgeSx}>{`+${hidden.length}`}</Box>
          </Tooltip>
        )}
      </Stack>
    </Stack>
  )
}

interface AnalyticsCustomizePanelProps {
  /** Every card the page can render. */
  cards: AnalyticsCardItem[]
  /** Ids currently on the canvas. */
  cardIds: string[]
  onAdd: (id: string) => void
  onRemove: (id: string) => void
  /** Notified on `dragstart` / `dragend` so the grid can size its drop placeholder. */
  onDragCardChange: (id: string | null) => void
  /** Put the ACTIVE view's cards and geometry back. See `useAnalyticsView.resetLayout`. */
  onResetLayout: () => void
  onClose: () => void
  /** Merged LAST, so a caller can add layout (the row gutter) without restating the panel. */
  sx?: SxProps<Theme>
}

export const AnalyticsCustomizePanel = ({
  cards,
  cardIds,
  onAdd,
  onRemove,
  onDragCardChange,
  onResetLayout,
  onClose,
  sx,
}: AnalyticsCustomizePanelProps) => {
  const theme = useTheme()
  const [query, setQuery] = useState('')
  /**
   * The type filter is MULTI-select, and an EMPTY selection means ALL TYPES.
   *
   * It was single-select with an explicit `__all__` sentinel option. Multi-select removes
   * that sentinel rather than keeping it alongside, because the two cannot coexist
   * coherently: "All types" ticked together with "Metric" has no meaning, and a sentinel
   * that silently clears the real selections is a control that undoes the user's last two
   * clicks without saying so.
   *
   * ## It filters by FORM only, and the reason is a missing link — not a design choice
   *
   * A metric's TYPE — its solution areas, whether it feeds ROI — is a real second axis and
   * it already exists: `MetricSolution`, `MetricRole` and `contributesToRoi` on
   * `IAnalyticsMetric` (`@repo/types/analytics`), built earlier in this epic for exactly
   * this. Adding it here as a second group of options was the intent and is NOT possible
   * today, because **no card in this library can be resolved to a registry metric.**
   *
   * Checked, rather than assumed. The three id spaces are disjoint:
   *
   *   library KPI tiles   `scans_count`, `certificates_count`, …   (`OVERVIEW_STAT_BOXES`,
   *                        keys of the totals response)
   *   library ROI cards   `mkt_geo_reach`, `legal_certificate_coverage`, …  (`ROICardDef`,
   *                        which carries no `metricId` field at all)
   *   `METRIC_REGISTRY`   `geographic_reach`, `certificate_coverage`, …
   *
   * So `METRIC_BY_ID[card.id]` misses on 19 of 19 — not "some", which is the number the
   * obvious guess produces. Every card would render an empty type, and the filter would be
   * a control that always returns nothing.
   *
   * ⚠️ **Do not close this by matching on title.** It looks like it would work — eleven of
   * the thirteen ROI cards have a near-identical registry counterpart — and it is wrong in
   * a way that is invisible afterwards. "Counterfeit Deflections" is a COUNT of incidents;
   * the registry's `counterfeit_deflection_value` is a CURRENCY metric with
   * `contributesToRoi: true`. Fuzzy-matching those makes a count card claim to feed the ROI
   * hero, which is precisely the kind of plausible-looking falsehood a provenance product
   * must not ship. The fix is an authored `metricId` on `ROICardDef`, decided per card by
   * someone who can say whether the two are the same measurement — a task, not a lookup.
   *
   * So "everything" is the ABSENCE of a filter, not a member of it — which is also the only
   * reading under which the control has no dead state. A multi-select whose empty value
   * meant "match nothing" would let the user reach an empty library by deselecting, with no
   * affordance on screen saying what to do about it. Deselecting the last type here simply
   * returns the full list, and the trigger says so: `placeholder="All types"` renders in the
   * trigger whenever nothing is selected, so the empty state is LABELLED rather than blank.
   */
  const [types, setTypes] = useState<AnalyticsCardCategory[]>([])

  /**
   * ── THE SOLUTION FILTER (VERA-260) ─────────────────────────────────────────────────
   *
   * The second axis the note above says was "a real second axis and NOT possible today".
   * It is possible now, and what changed is precisely the thing that note said was
   * missing: the link between a library card and a classification. It did not arrive the
   * way that note anticipated (an authored `metricId` per card, resolving into
   * `METRIC_REGISTRY`) — that route is still open and still undecided per card, because
   * asserting a card IS a registry metric carries a claim about the ROI hero that a
   * taxonomy does not.
   *
   * Instead `ANALYTICS_METRIC_SOLUTIONS` is keyed on the LIBRARY's own id space, so no
   * cross-space match is required and the "misses on 19 of 19" problem does not arise.
   * It is total over that space, so the filter cannot go stale in either direction.
   *
   * Same empty-means-all convention as `types` above, for the reason stated there: a
   * multi-select whose empty value meant "match nothing" lets a reader deselect their way
   * into a blank panel with nothing on screen saying how to leave.
   */
  const [solutions, setSolutions] = useState<AnalyticsSolutionCategory[]>([])

  const onCanvas = useMemo(() => new Set(cardIds), [cardIds])

  /**
   * ── THE LIST IS METRIC ROWS ONLY (VRG-481) ─────────────────────────────────────────
   *
   * Every chart and map card the page can render is a `(metric, display)` VARIANT of a
   * metric that is already in this list, and its display is offered by that metric's own
   * `Display as` chips. Listing it again as its own row is the redundancy this change
   * removes — the reader saw `Total Scans` and `Total Scans over time` and had no way to
   * tell they were one measurement.
   *
   * Filtered on the ID rather than on `category`, deliberately. `category` says how a card
   * is DRAWN, which is exactly what stops being a row property here; the id says whether
   * the card IS a variant of something else, which is the actual question. It also cannot
   * go stale: a variant id is built by one function and parsed by its inverse.
   *
   * The `cards` PROP is untouched — the canvas still needs every card, variants included.
   * Only what this panel LISTS narrows.
   */
  /*
    ── PLUS THE PRESETS: variant cards NO CHIP CAN PRODUCE (VERA-260) ─────────────────

    The rule above is sound and unchanged — a variant is listed by its metric's own
    `Display as` chips, so listing it again is the redundancy that made `Total Scans` and
    `Total Scans over time` two rows for one measurement. But the rule's premise is that
    the chips can REACH every variant, and grouping broke that: a `Display as -> Map` chip
    places the map at its DEFAULT grouping (`analyticsDefaultVariantCardId`), and there is
    no per-grouping chip. So `scans_count::map@country` — the card `scans_by_country`
    became — is a variant no affordance in this panel can offer.

    Hence a PRESET: a variant row that IS listed, precisely because it is unreachable
    otherwise. `analyticsIsLibraryPresetCardId` derives that from capability rather than
    naming ids, so the set stays correct as the classification is corrected — the day
    `certificates` becomes country-groupable (its `COUNTRY_GROUPABLE` entry names the
    server-side fix) it acquires a preset row with no edit here, and a metric whose pin
    grouping is retired loses one the same way.

    ⚠️ This is the honest reading of the old rule, not an exception to it. "Do not list a
    variant its metric already offers" and "do list one it does not" are the same sentence.
  */
  const metricRows = useMemo(
    () =>
      cards.filter(
        card =>
          !parseAnalyticsVariantCardId(card.id) || analyticsIsLibraryPresetCardId(card.id),
      ),
    [cards],
  )

  /**
   * Type options, from the ONE registry — not collected off the cards.
   *
   * Walking `cards` was the previous version and it had the failure backwards: a
   * category that no card currently uses vanished from the filter (fine), but a card
   * carrying a category the registry does not know about got an option with no badge and
   * no label discipline. Reading the registry means the filter and the badge can only
   * ever say the same words, and a bad `category` is a TypeScript error at the card's
   * declaration rather than a silent oddity here.
   *
   * Each option carries its category's GLYPH in its category's hue, from the same
   * `ANALYTICS_CARD_CATEGORIES` colours the badges on the rows below are painted from. That
   * is the whole reason the colours live in the registry: the dropdown and the badges are
   * one system, and a reader who filters to Chart should see the mark they were scanning
   * for. A second colour table here would let them drift apart silently.
   *
   * ORDER and LABELS come from the registry; PRESENCE comes from the cards. That split is
   * the point, and it is not the bug the paragraph above describes — that one was deriving
   * the *labels* from whatever string a card carried. An option for a category no card
   * uses can only ever return an empty list, so it is a control that is broken on arrival;
   * suppressing it is why `table` can be added to the registry the day a table card exists
   * without anyone having to remember to wire a filter option at the same time.
   */
  const typeOptions = useMemo(() => {
    /**
     * ── IT FILTERS BY CAPABILITY NOW, NOT BY THE ROW'S OWN TYPE (VRG-481) ────────────
     *
     * Collapsing the library to one row per metric made every row a `Metric`, which left
     * the old type filter with a single option that could only ever return the whole
     * list. That is a dead control, so the axis moved rather than the control going: the
     * question is now "which of my components can be shown as a chart / as a map",
     * answered by `analyticsDisplayCapability` — the same derivation the `Display as` chips
     * are drawn from, so the filter and the chips cannot disagree.
     *
     * `metric` is EXCLUDED as an option. Every row is capable of it, so it would be the
     * one option that never narrows anything — the same defect one level down.
     *
     * ORDER, LABELS and HUES still come from `ANALYTICS_CARD_CATEGORIES`, so the glyph a
     * reader filters by is the glyph on the chips they were scanning for. PRESENCE still
     * comes from the cards: a display no metric can take is a filter option that returns
     * nothing, so it is suppressed — which today keeps both, and would silently drop
     * `map` if the scans-map endpoint ever stopped locating anything.
     */
    const present = new Set(metricRows.flatMap(rowDisplays))
    return ANALYTICS_CARD_CATEGORY_ORDER.filter(id => present.has(id)).map(id => ({
      label: ANALYTICS_CARD_CATEGORIES[id].label,
      value: id as string,
      icon: <AnalyticsCategoryGlyph category={id} />,
    }))
  }, [metricRows])

  /**
   * Solution options, from the ONE vocabulary — same construction as `typeOptions`.
   *
   * ORDER, LABELS and BLURBS come from `analyticsSolutions`; PRESENCE comes from the
   * cards, so a category no metric currently carries is not offered as an option that
   * could only ever return an empty list. That split matters more here than it does for
   * displays, because the classification is authored and expected to be corrected: moving
   * the last Sustainability metric out of that category should retire the option, not
   * leave a dead one behind for someone to discover by clicking it.
   */
  const solutionOptions = useMemo(() => {
    const present = new Set(metricRows.flatMap(card => analyticsMetricSolutions(card.id)))
    /*
      NO per-option blurb: `SelectItem` carries `label`, `value` and `icon` only, and a
      menu row is the wrong place for a sentence anyway — it would set the menu's width
      from its longest description. `ANALYTICS_SOLUTION_BLURBS` is rendered on the BADGE's
      tooltip instead, which is where a reader meets an unfamiliar category attached to a
      specific metric rather than as an abstract option.

      ⚠️ `icon` NOW, and this note used to argue the opposite ("there is no established
      glyph for a solution, and inventing six would be six new symbols to learn for no
      scanning benefit"). The premise held while the BADGE had no glyph — a mark that
      appeared only in a menu really would have been a symbol with nowhere to be learned.
      The badges carry it now, from the same table, so the menu is where a reader FIRST
      meets the mark they will then recognise on a row. That is the identical argument
      `typeOptions` already makes for its own glyphs, which are the marks the `Display as`
      chips carry.

      ONE table for both (`ANALYTICS_SOLUTION_ICONS`), never a second map here — see its
      header for why, and for what each glyph depicts.
    */
    return ANALYTICS_SOLUTION_ORDER.filter(id => present.has(id)).map(id => {
      const Glyph = ANALYTICS_SOLUTION_ICONS[id]
      return {
        label: ANALYTICS_SOLUTION_LABELS[id],
        value: id as string,
        /* `text.secondary`, matching `AnalyticsCategoryGlyph`'s default in the type
           filter beside it: the mark reads as part of its label, not as a second one. */
        icon: (
          <Stack component="span" aria-hidden sx={{ color: 'text.secondary', display: 'inline-flex' }}>
            <Glyph size={14} strokeWidth={1.5} />
          </Stack>
        ),
      }
    })
  }, [metricRows])

  /**
   * Search AND type AND solution — a card must satisfy all three, not any.
   *
   * ⚠️ CONJUNCTIVE ACROSS the controls, DISJUNCTIVE WITHIN each one, and the asymmetry is
   * deliberate rather than an oversight. Across: union would make adding a search term
   * WIDEN the result set, which inverts what a reader expects from typing into a box below
   * a filter they already set. Within: a multi-select's ticked values are alternatives —
   * "Chart, Map" plainly means either, and "Brand protection, Consumer engagement" means
   * either too, which the panel now says out loud (`solutionHelp`) because that one is the
   * reading a reader could get wrong.
   *
   * Search matches the TITLE only — the description is prose and would match noise.
   */
  const shown = useMemo(() => {
    const q = query.trim().toLowerCase()
    return metricRows.filter(card => {
      // Empty selection is not a filter. See `types` above. A selection matches a row if
      // the row CAN be shown that way — ANY of the selected displays, not all of them,
      // because "Chart, Map" ticked together plainly means "either".
      if (types.length > 0) {
        const displays = rowDisplays(card)
        if (!types.some(display => displays.includes(display))) return false
      }
      if (solutions.length > 0) {
        const carried = analyticsMetricSolutions(card.id)
        if (!solutions.some(solution => carried.includes(solution))) return false
      }
      if (!q) return true
      return (card.title ?? card.id).toLowerCase().includes(q)
    })
  }, [metricRows, query, types, solutions])

  /**
   * The empty state's sentence, COMPOSED from the active constraints (VERA-260).
   *
   * See `emptyPrefix` for why this is generated rather than enumerated. Hoisted out of the
   * JSX for one specific reason: the clause list can be EMPTY — a panel handed no cards at
   * all renders the empty state with no filter set — and `Array.prototype.reduce` with no
   * initial value THROWS on an empty array. In the JSX that is an exception during render,
   * which takes the whole panel down rather than showing a bare message.
   */
  const emptyMessage = useMemo(() => {
    /*
      ⚠️ ANNOTATED `(string | null)[]`, and it is not decoration. `ANALYTICS_LIBRARY_COPY`
      is `as const`, so an un-annotated literal here infers an array of STRING LITERAL
      types unioned with `null` — against which `(clause): clause is string` is rejected
      outright (TS2677: a type predicate must be assignable to its parameter's type), and
      the `reduce` below then produces a combinatorial union of every possible joined
      sentence rather than `string`. Widening at the declaration fixes both.
    */
    const candidates: (string | null)[] = [
      query.trim() ? ANALYTICS_LIBRARY_COPY.emptyBySearch : null,
      types.length > 0 ? ANALYTICS_LIBRARY_COPY.emptyByTypes : null,
      solutions.length > 0 ? ANALYTICS_LIBRARY_COPY.emptyBySolutions : null,
    ]
    const clauses = candidates.filter((clause): clause is string => clause !== null)

    // No filter is active, so there is nothing to undo and nothing to name. The list is
    // simply empty — say that, rather than a sentence trailing off after "No components".
    if (clauses.length === 0) return `${ANALYTICS_LIBRARY_COPY.emptyPrefix} to show.`

    /* "a, b and c" rather than "a, b, c" — the list reads as a sentence, and the
       conjunction is what says ALL of these are applied at once, which is the fact a
       reader needs in order to work out which one to undo. */
    const joined = clauses.reduce((sentence, clause, i, all) =>
      i === all.length - 1 ? `${sentence} and ${clause}` : `${sentence}, ${clause}`,
    )
    return `${ANALYTICS_LIBRARY_COPY.emptyPrefix} ${joined}.`
  }, [query, types, solutions])

  const handleDragStart = useCallback(
    (id: string) => (e: React.DragEvent) => {
      // react-grid-layout reads the drop through the native HTML5 drag events, and
      // Firefox refuses to start a drag at all unless SOME data is set.
      e.dataTransfer.setData('text/plain', id)
      e.dataTransfer.effectAllowed = 'copy'
      onDragCardChange(id)
    },
    [onDragCardChange],
  )

  return (
    <Box
      component="aside"
      aria-label={ANALYTICS_LIBRARY_COPY.landmark}
      sx={{
        flexShrink: 0,
        width: ANALYTICS_CUSTOMIZE_PANEL_WIDTH,
        /**
         * Sticky, and it MUST stay sticky: this panel is a drag source, and a drag source
         * that scrolls out from under a captured pointer strands the user mid-gesture with
         * no recovery but letting go. Sticky rather than fixed so it never has to know the
         * app sidebar's width — it takes its horizontal position from the flex row and
         * only pins vertically.
         *
         * `top: 0` is NOT "flush to the window" — the scroll container's own 24px padding
         * is the inset, so this pins the panel 24px down. See `PANEL_VIEWPORT_RESERVE_PX`,
         * where both halves of the arithmetic are measured; the CSS number and the visible
         * inset genuinely differ here, and writing `top: 24px` produces 48.
         *
         * It went to `top: 0` / `100vh` when the panel became a sidebar, on the reasoning
         * that a docked sidebar runs edge to edge. That is true of a sidebar rendered by
         * `Layout` — which is why `AskPanel` can do it — and false of this one, which is
         * PAGE-scoped and therefore starts ~271px down, below the page header and the
         * action line. Measured before the fix: top 271.5, height 800, so the bottom sat
         * at 1071 in an 800px viewport and the panel was cut off at both ends.
         *
         * The height is a CONSTANT expression, deliberately not a measurement. The obvious
         * version — measure the panel's own top on mount and subtract it — was tried and
         * is wrong here: on mount the canvas beside it has not yet reflowed to its narrower
         * width, so the reading is taken against a layout that is about to change, and it
         * measured 404px against the 483px the panel occupies.
         *
         * `vhMinus` rather than a bare `calc(100vh - …)` so the panel tracks the text-zoom
         * preference like every other full-height surface in the app.
         */
        /**
         * ── NOT STICKY, AND NOT SELF-SIZING — the RAIL owns both now ────────────────────
         *
         * This carried `position: sticky`, its own `top`, and `height: vhMinus(...)`. All
         * three moved up to the `Collapse` in `AnalyticsPage`, and the move is a BUG FIX,
         * not a tidy-up.
         *
         * MUI's horizontal `Collapse` sets `wrapper.style.position = 'absolute'` during its
         * `enter` phase — it has to, to measure the child's natural width before animating
         * to it — and restores it one phase later in `entering`. While that is in effect
         * the CONTAINING BLOCK of everything inside changes, so a sticky descendant
         * resolves its offset against a different box for the first frames of the
         * transition and then snaps back. Measured: this panel rendered at top 483
         * throughout the slide and jumped to 271.5 the moment it settled — a 211.5px
         * vertical lurch, exactly the sticky header's height, which is what "slides in and
         * then expands up" was describing. The width was animating correctly the whole
         * time; the vertical jump was a second, unwanted motion riding along with it.
         *
         * Making the ANIMATING element the sticky one removes the interaction entirely:
         * the wrapper's absolute phase now happens INSIDE a box whose height and offset are
         * already fixed, so it can no longer move anything the user can see.
         *
         * `height: 100%` — the rail states the real height (a `calc` off the viewport), and
         * this just fills it. Deliberately NOT a second `vhMinus` call here: two places
         * computing the same height is how they drift, and only one of them can be the one
         * the sticky offset agrees with.
         */
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        bgcolor: 'background.paper',
        /**
         * NO negative margin. The panel sits inside the page gutter like everything else on
         * the page, so its right edge lands on the canvas cards' right edge — measured 1256
         * at a 1280 window, for both. (It was 1232 while the page carried its own `p={6}` on
         * top of `Layout`'s; the pair still agree, they just agree 24px further out now.)
         *
         * It used to carry `mr: -48px` to break out of `Layout`'s padding AND the page's,
         * reaching the window edge. That was load-bearing for a flush, square-cornered
         * sidebar and is exactly wrong for an inset one: a rounded, fully-bordered panel
         * hard against the window has its right border on the window edge and its corners
         * clipped. Dropping it also removes a documented coupling to `Layout`'s padding that
         * nothing could read from `sx`, and with it the `useFlexGap` hazard that used to
         * silently zero the margin out.
         */
        /**
         * A FULL border and the card radius — 12px, measured off a rendered card on the
         * canvas rather than read off a token, since `borderRadius: 3` is only 12px while
         * the 4px spacing base holds.
         *
         * This reverses an earlier call to use `AskPanel`'s treatment (one `borderLeft`,
         * no radius), and the reversal follows from the geometry rather than from taste. A
         * single left divider is the correct edge for a panel that is FLUSH to the window
         * on its other three sides — there, the window supplies the boundary. Once inset,
         * there is nothing to supply it, and three unbordered sides read as a torn edge.
         * Inset plus a full border is the shared `Card`'s treatment because it says "an
         * object sitting on the page", which is now exactly what this is.
         */
        border: `1px solid ${theme.palette.divider}`,
        borderRadius: 3,
        // Clip the header/footer rows to the new radius — without this the header's own
        // bottom border and the first row's hover fill square off the top corners.
        overflow: 'hidden',
        ...(Array.isArray(sx) ? Object.assign({}, ...sx) : ((sx as object) ?? {})),
      }}
    >
      <Stack
        direction="row"
        alignItems="center"
        spacing={2}
        sx={{ flexShrink: 0, px: 4, py: 3, borderBottom: `1px solid ${theme.palette.divider}` }}
      >
        <Typography variant="body2" sx={{ fontWeight: 600, flex: 1, minWidth: 0 }} noWrap>
          {ANALYTICS_LIBRARY_COPY.title}
        </Typography>

        {/**
          * Reset lives HERE rather than beside Views, and the placement is an argument
          * about what it resets.
          *
          * `ViewsDropdown`'s footer already carries a "Reset to default" that clears the
          * active view and returns to the Overview seed. This one puts the ACTIVE view's
          * arrangement back and leaves the selection alone (see `resetLayout`). Two
          * resets one row apart in the same menu, meaning different things, is how you
          * get a user who trusts neither — so the arrangement reset sits with the
          * arrangement surface. This panel IS that surface: it is where cards are added,
          * removed and dragged from, so "put the cards back" belongs to it.
          *
          * The cost, stated: after a bad drag with the panel closed you must open
          * Customize to reach it. That is one click on a labelled control whose whole
          * name is "arrange the cards", and it buys an unambiguous pair of resets.
          */}
        <Tooltip title={ANALYTICS_LIBRARY_COPY.resetTooltip}>
          <IconButton
            size="small"
            onClick={onResetLayout}
            aria-label={ANALYTICS_LIBRARY_COPY.resetAria}
          >
            <RotateCcwIcon strokeWidth={1} size={18} />
          </IconButton>
        </Tooltip>

        {/* COLLAPSE, not close. The glyph is already the panel-collapse mark (the same
            one AskPanel uses) and the label now agrees with it: nothing is being
            dismissed or removed, a panel is being folded away, and §38 reserves `X` for
            removal. */}
        <Tooltip title={ANALYTICS_LIBRARY_COPY.collapseTooltip}>
          <IconButton size="small" onClick={onClose} aria-label={ANALYTICS_LIBRARY_COPY.collapseAria}>
            <PanelRightCloseIcon strokeWidth={1} size={18} />
          </IconButton>
        </Tooltip>
      </Stack>

      {/* STICKY controls. Outside the scroll container rather than `position: sticky`
          inside it — the list is the only thing that scrolls, so a sticky child would
          be solving a problem the layout does not have, and would need a background to
          avoid rows showing through it. */}
      <Stack spacing={3} sx={{ flexShrink: 0, px: 4, py: 3 }}>
        <Search
          value={query}
          onChange={setQuery}
          onClear={() => setQuery('')}
          placeholder={ANALYTICS_LIBRARY_COPY.searchPlaceholder}
          fullWidth
        />
        {/* The house multi-select (§28), not a bare MUI `Select`: it already owns the
            right-aligned check on selected rows, the sticky "N selected" header, the
            field chrome, and the one-line `"KPI, ROI +2"` trigger label that keeps a
            four-way selection from wrapping a 340px sidebar. */}
        <SelectField
          multiple
          placeholder={ANALYTICS_LIBRARY_COPY.allTypes}
          // Kept as a PREFIX once a display is picked — "Display as: Chart" — rather
          // than the trigger falling back to a bare "Chart" with nothing saying which
          // control it came from. See `allTypes`'s own note.
          valuePrefix={ANALYTICS_LIBRARY_COPY.allTypes}
          options={typeOptions}
          value={types}
          onChange={v => setTypes((Array.isArray(v) ? v : [v]) as AnalyticsCardCategory[])}
        />
        {/*
          VERA-260 — the SOLUTION filter, below the display one.

          Below rather than above because the two narrow on different kinds of question and
          the order should run from the concrete to the abstract: "can I draw this as a
          map" is answered by looking at the card, whereas "is this evidence for brand
          protection" is a claim about what the figure MEANS. A reader scanning the panel
          top to bottom meets search, then form, then meaning.
        */}
        <Stack spacing={2}>
          <SelectField
            multiple
            placeholder={ANALYTICS_LIBRARY_COPY.allSolutions}
            valuePrefix={ANALYTICS_LIBRARY_COPY.allSolutions}
            options={solutionOptions}
            value={solutions}
            onChange={v =>
              setSolutions((Array.isArray(v) ? v : [v]) as AnalyticsSolutionCategory[])
            }
          />
          {/*
            The OR semantics, stated — and ONLY while a selection exists.

            A permanent caption would be a sentence explaining a control nobody has touched
            yet, which is the noise that teaches readers to skip help text. It appears at
            the moment the ambiguity does: the second a solution is ticked, the reader has a
            filtered list in front of them and a reason to wonder what "and" would have
            meant. One tick is enough — the reading is the same for one as for three, and
            waiting for two would hide the explanation from the reader most likely to
            generalise wrongly from a single-selection result.
          */}
          {solutions.length > 0 && (
            <Typography variant="body3" color="text.secondary" sx={{ lineHeight: 1.5 }}>
              {ANALYTICS_LIBRARY_COPY.solutionHelp}
            </Typography>
          )}
        </Stack>
      </Stack>

      <Box sx={{ flex: 1, minHeight: 0, overflowY: 'auto', px: 4, pb: 4 }}>
        {shown.length === 0 ? (
          /* Name the control(s) that emptied the list, so the way back is obvious. With
             two filters this was three literals — every combination — and a third makes
             that seven, which is where enumeration stops being maintained and becomes a
             generic "No results". Composed from whichever constraints are ACTIVE instead,
             so the message is right for all seven and for any filter added later. */
          <Typography variant="body3" color="text.secondary" sx={{ display: 'block', py: 4 }}>
            {emptyMessage}
          </Typography>
        ) : (
          <Stack spacing={2}>
            {shown.map(card => {
              const added = onCanvas.has(card.id)
              const name = card.title ?? card.id
              return (
                <Stack
                  key={card.id}
                  direction="row"
                  alignItems="flex-start"
                  spacing={2}
                  // ALWAYS draggable (VERA-260) — see the header note above. `added`
                  // still gates the +/X control and the "chosen" styling below; it no
                  // longer withdraws the grip.
                  draggable
                  onDragStart={handleDragStart(card.id)}
                  onDragEnd={() => onDragCardChange(null)}
                  sx={{
                    p: 2,
                    borderRadius: 2,
                    border: '1px solid',
                    /**
                     * ── THE CANONICAL SELECTABLE-CARD TREATMENT (§34) ──────────────────
                     *
                     * These rows were `divider` on `transparent`, with `action.hover` doing
                     * double duty as both the hover tint AND the already-placed state — so
                     * the two were the SAME colour and the only thing telling them apart
                     * was that one of them followed your cursor. The greys also read muddy,
                     * which is what prompted this.
                     *
                     * The repair is not a lighter grey, it is the house tokens: rest is
                     * `primary[100]` on `background.paper`, which is what a `FeaturesPage`
                     * card wears. That reads cleaner precisely because the tokens are tuned
                     * — and it makes this the SAME OBJECT as every other selectable card in
                     * the app rather than a fourth bespoke surface.
                     *
                     * ⚠️ NOT borderless. These rows are DRAG SOURCES: a borderless row
                     * reads as text, a bordered one reads as an object you can pick up, and
                     * that affordance is the whole reason this panel is a sidebar rather
                     * than the popover it replaced. The border is load-bearing, so
                     * "cleaner" had to mean a better border, never no border.
                     *
                     * §34's axis, applied: TINT = considering, BORDER = chosen.
                     *   rest      `primary[100]` edge, paper fill
                     *   hover     brand tint only, edge UNCHANGED
                     *   on canvas brand[500] edge + a stronger brand tint
                     *
                     * A row already on the canvas is still the "chosen" state — it is on
                     * the dashboard and its trailing control has become a remove — so it
                     * keeps the border. ⚠️ It is NO LONGER "no longer draggable" (VERA-260):
                     * the grip stays live so the same metric can be dragged onto a
                     * different chart/map to join it there too — see the header note.
                     */
                    borderColor: added ? 'brand.500' : 'primary.100',
                    backgroundColor: added
                      ? alpha(theme.palette.brand[500], 0.1)
                      : 'background.paper',
                    // Always 'grab': the row is always draggable now. The added/chosen
                    // state is carried entirely by the border + fill above.
                    cursor: 'grab',
                    '&:active': { cursor: 'grabbing' },
                    /**
                     * Hover changes the FILL ONLY — never the border. §34 is explicit that a
                     * brand border on hover collides with the chosen state and destroys the
                     * distinction between "considering" and "picked", which is exactly the
                     * bug this row had when both states shared `action.hover`.
                     *
                     * An added row keeps its own persistent tint rather than a hover ripple
                     * on top of it (the ripple would LIGHTEN it, which reads as fading, not
                     * highlighting) — cosmetic only; the row is still fully draggable.
                     */
                    '&:hover': added
                      ? undefined
                      : { backgroundColor: alpha(theme.palette.brand[500], 0.06) },
                  }}
                >
                  <Box
                    aria-hidden
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      height: 20,
                      // ALWAYS visible (VERA-260) — the grip is no longer a lie once the
                      // row is always draggable. It used to go `transparent` when `added`
                      // was true, which read as "you cannot drag this" at exactly the
                      // moment you still could (drag-to-compose onto another chart).
                      color: 'text.disabled',
                      flexShrink: 0,
                    }}
                  >
                    <GripVerticalIcon size={16} strokeWidth={1.5} />
                  </Box>

                  {/**
                    * TWO ROWS, and the split is the whole point of this layout.
                    *
                    * ## The bug it fixes
                    *
                    * The type and the control used to be a THIRD flex item in the row,
                    * siblings of this column — so they took their width out of the column
                    * that holds BOTH the title and the description. The description was
                    * therefore squeezed to the leftover: measured 150.7px of a 306px row,
                    * wrapping to FOUR lines against a half-width column, for no reason
                    * other than that a two-word label sat somewhere above it.
                    *
                    * Now the type and the control live on the TITLE's row, and the
                    * description is a sibling row beneath them at the column's full width.
                    * Nothing to the right of the title constrains the prose any more.
                    *
                    * ## Order on the title row
                    *
                    * Title (flexes, ellipsizes) · control (fixed, outermost).
                    *
                    * ⚠️ The TYPE BADGE that used to sit between them is gone (VRG-481).
                    * With one row per metric it said "Metric" on every row — a column of
                    * identical labels carrying no information — and while the rows were
                    * pairs it actively CONFLICTED with the chips below: the badge named
                    * this pair's display while the chips offered the others, so two
                    * controls described one axis and appeared to disagree. The `Display as`
                    * chips are the state now, and they are the only thing that says it.
                    *
                    * The title still gives way first when a row is narrow — it has more to
                    * give, and the control is a fixed 24px click target that must sit at
                    * the same x on every row (§26/§33).
                    */}
                  {/**
                    * ── THE COLUMN'S GAPS ARE NOT UNIFORM, AND THAT IS THE POINT ────────
                    *
                    * `spacing={0}`, with each block below the title owning its own `mt`.
                    * It was `spacing={0.5}` — a flat **2px** between all four blocks — and
                    * the row read as one compressed slab: title, description, `Solution`
                    * and `Display as` all equally close, so nothing said which belonged
                    * with which.
                    *
                    * Measured gaps now, and the ladder is the grouping:
                    *
                    *   title -> description         4px   one unit: what this metric is
                    *   description -> Solution      8px   still metadata, one step out
                    *   Solution -> Display as      12px   the CONTROLS. The widest gap in
                    *                                      the row, because it is the one
                    *                                      boundary that changes what a
                    *                                      thing DOES rather than what it
                    *                                      says.
                    *
                    * 12px is the house button-group gap (§29) on `@repo/ui`'s 4px base, so
                    * `spacing={3}`; 8px is the house label->control gap (§31). Neither is
                    * a number picked to look right.
                    *
                    * ⚠️ The gap belongs to the CHILD (`SolutionBadges`, then
                    * `AnalyticsDisplayPicker` set their own `mt`), not to this Stack. Five
                    * metrics — the primitives — carry no solutions and render no badge row
                    * at all, and a gap owned by the parent would leave that row's 8px
                    * behind as a hole. Owned by the child, the space leaves with it.
                    */}
                  <Stack spacing={0} sx={{ flex: 1, minWidth: 0 }}>
                    <Stack direction="row" alignItems="center" spacing={2}>
                      {/*
                        ⚠️ WRAPS. It used to be `noWrap`, and the ellipsis was measured
                        eating four card names at a 340px panel:

                          Authenticity Verification Rate   needed 39px more than it had
                          Total Certificates by location   19px
                          Total Certificates over time     15px
                          Transfer Completion Rate         11px

                        The title IS what the row is — §49's argument about table headers
                        applies with more force here, because a library row has no detail
                        view and no tooltip behind it. "Total Certificates by loc…" and
                        "Total Certificates over…" are not merely clipped, they are the two
                        rows a reader most needs to tell apart.

                        Shortening the labels was the cheaper fix and is the wrong one:
                        two of the four are PRE-EXISTING ROI card names this change did not
                        introduce, so trimming the generated ones would leave the panel
                        still truncating and hide that it always had. And the room is not
                        one word — the title column measures ~174px of the 340, once the
                        grip, the type label and the control have taken theirs.

                        `minWidth: 0` still earns its place: without the zero minimum the
                        title's min-content width floors the flex row and pushes the type
                        label out. `overflowWrap` is the guard for a title with no space to
                        break at, which no card has today and one could.
                      */}
                      <Typography
                        variant="body2"
                        sx={{
                          fontWeight: 500,
                          flex: 1,
                          minWidth: 0,
                          overflowWrap: 'anywhere',
                          // The row is `alignItems: 'center'`, so a two-line title centres
                          // against the type label and the control rather than sitting
                          // under them. 1.35 keeps two lines tight enough to read as one
                          // title instead of as a title and a stray line.
                          lineHeight: 1.35,
                        }}
                      >
                        {name}
                      </Typography>
                      {/* X, not a trash can: removing a card takes it off the canvas and
                          destroys nothing (§38). Add is a plus for the same reason. */}
                      <Tooltip
                        title={
                          added
                            ? ANALYTICS_LIBRARY_COPY.remove(name)
                            : ANALYTICS_LIBRARY_COPY.add(name)
                        }
                      >
                        <IconButton
                          size="small"
                          aria-label={
                            added
                              ? ANALYTICS_LIBRARY_COPY.removeAria(name)
                              : ANALYTICS_LIBRARY_COPY.addAria(name)
                          }
                          onClick={() => (added ? onRemove(card.id) : onAdd(card.id))}
                          sx={{ flexShrink: 0, color: 'text.secondary' }}
                        >
                          {added ? (
                            <XIcon size={16} strokeWidth={1.5} />
                          ) : (
                            <PlusIcon size={16} strokeWidth={1.5} />
                          )}
                        </IconButton>
                      </Tooltip>
                    </Stack>
                    {card.summary && (
                      // 1.5 line height: descriptive copy at the default leading reads
                      // dense (§15). `mt: 1` (4px) — the description belongs to the title,
                      // so it sits closer to it than anything below sits to either.
                      <Typography
                        variant="body3"
                        color="text.secondary"
                        sx={{ lineHeight: 1.5, mt: 1 }}
                      >
                        {card.summary}
                      </Typography>
                    )}
                    {/*
                      VERA-260 — WHAT THIS METRIC IS EVIDENCE FOR, before any drag begins.

                      Placed ABOVE the `Display as` row, not below it: these are metadata
                      and those are controls. Static-before-interactive is the order that
                      lets a reader stop scanning at the first thing they can click, and it
                      keeps the row's one interactive region contiguous with the row's
                      bottom edge.

                      Each row is LABELLED for the same reason — one chip row above another
                      chip row is two things a reader has to tell apart. `Display as`
                      already carried a label; this one matches it exactly.

                      ⚠️ ORDER AND LABELS WERE ONCE THE ONLY THINGS TELLING THEM APART, and
                      they were not enough: the two rows rendered identical outlined pills,
                      one passive and one clickable-and-draggable, and a reader had to click
                      to find out which. The badges are now FILLED AND EDGELESS and the
                      chips OUTLINED, so the distinction survives without reading either
                      label — see `SolutionBadges`' `badgeSx` and the display picker's grip.
                      This ordering still matters; it is just no longer load-bearing alone.

                      Renders NOTHING for the five primitives, which carry `[]` — see
                      `ANALYTICS_METRIC_SOLUTIONS`. Its `mt` goes with it, so those rows
                      close up rather than leaving a gap where a badge row would have been.
                    */}
                    <SolutionBadges cardId={card.id} />
                    {/*
                      VRG-467 / VRG-481 — WHICH DISPLAYS THIS METRIC SUPPORTS, stated
                      before any drag begins.

                      Unconditional now. It used to be guarded on `category === 'metric'`
                      because the list also held chart and map rows, and offering to redraw
                      a chart as a map would have been offering a card that already existed
                      elsewhere in the same list under its own name. `metricRows` removes
                      those rows, so every row reaching here is a metric and the guard was
                      a condition that could no longer be false.

                      The component renders NOTHING when the metric supports no other
                      display — which is 16 of the 18 rows — so the absent guard does not
                      put a dead row under every card. See its header for why that rule is
                      the whole row and not the individual chips.

                      The capability itself is derived in `analyticsDisplay` from the data
                      path that would have to draw the thing — never a flag on a metric —
                      and an incapable affordance that has a capable sibling renders
                      disabled with the reason. See `AnalyticsDisplayPicker` for why
                      disabled rather than hidden, which is the opposite of what §46 says
                      for record state and follows from §46's actual axis: nobody can
                      resolve a capability block.
                    */}
                    <AnalyticsDisplayPicker
                      cardId={card.id}
                      cardTitle={name}
                      placedIds={onCanvas}
                      onAdd={onAdd}
                      onRemove={onRemove}
                      onDragCardChange={onDragCardChange}
                    />
                  </Stack>

                </Stack>
              )
            })}
          </Stack>
        )}
      </Box>
    </Box>
  )
}
